import type {
  CreateOnDemandRequestsInput,
} from '@/integrations/agreements/client';

/** Estado do formulário da solicitação, já no formato que a tela edita. */
/**
 * Uma data da solicitação. Sem turno: o turno é pedido de caçamba (obra e via pública), e a
 * coleta segue a janela combinada no contrato.
 */
export interface RequestFormDate {
  /** `YYYY-MM-DD`; vazio enquanto a pessoa não escolheu. */
  date: string;
}

export interface RequestFormPerson {
  name: string;
  phone: string;
  email: string;
}

export interface RequestFormState {
  dates: RequestFormDate[];
  quantity: string;
  requester: RequestFormPerson;
  hasCompanion: boolean;
  companion: RequestFormPerson;
  photoUrls: string[];
  observations: string;
}

/** Limite do serviço: cada data vira uma solicitação, até 31 num pedido. */
export const MAX_DATES = 31;

export const emptyPerson = (): RequestFormPerson => ({ name: '', phone: '', email: '' });

export function initialRequestForm(requester: Partial<RequestFormPerson> = {}): RequestFormState {
  return {
    dates: [{ date: '' }],
    quantity: '1',
    requester: { ...emptyPerson(), ...requester },
    hasCompanion: false,
    companion: emptyPerson(),
    photoUrls: [],
    observations: '',
  };
}

export type RequestFormErrors = Partial<Record<
  'dates' | 'quantity' | 'requesterName' | 'requesterPhone' | 'requesterEmail'
  | 'companionName' | 'companionPhone' | 'companionEmail',
  string
>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const digits = (value: string) => value.replace(/\D/g, '');

function validatePerson(
  person: RequestFormPerson,
  prefix: 'requester' | 'companion',
  errors: RequestFormErrors,
) {
  const name = person.name.trim();
  if (name.length < 4) errors[`${prefix}Name`] = 'Informe o nome completo, com pelo menos 4 letras.';

  if (digits(person.phone).length < 10) errors[`${prefix}Phone`] = 'Informe o telefone com DDD.';

  if (person.email.trim() && !EMAIL.test(person.email.trim())) {
    errors[`${prefix}Email`] = 'E-mail inválido — confira o endereço.';
  }
}

/**
 * Mesmas regras do serviço para o que dá para conferir na tela. Antecedência e vigência também
 * são conferidas aqui só para orientar: o serviço confere de novo e é quem decide.
 */
export function validateRequestForm(
  form: RequestFormState,
  limits: { minDate: string; maxDate?: string | null; minDateMessage?: string },
): RequestFormErrors {
  const errors: RequestFormErrors = {};
  const chosen = form.dates.map((d) => d.date).filter(Boolean);

  if (!chosen.length) {
    errors.dates = 'Escolha ao menos uma data.';
  } else if (chosen.length !== form.dates.length) {
    errors.dates = 'Escolha a data de cada linha ou remova a linha vazia.';
  } else if (new Set(chosen).size !== chosen.length) {
    errors.dates = 'Há datas repetidas. Cada data vira uma solicitação.';
  } else if (chosen.some((d) => d < limits.minDate)) {
    errors.dates = limits.minDateMessage
      ?? `A antecedência mínima deste contrato permite datas a partir de ${toBr(limits.minDate)}.`;
  } else if (limits.maxDate && chosen.some((d) => d > limits.maxDate!)) {
    errors.dates = `O contrato termina em ${toBr(limits.maxDate)}. Escolha datas até esse dia.`;
  }

  const quantity = Number(form.quantity);
  if (!Number.isInteger(quantity) || quantity < 1) errors.quantity = 'Informe a quantidade, a partir de 1.';

  validatePerson(form.requester, 'requester', errors);
  if (form.hasCompanion) validatePerson(form.companion, 'companion', errors);

  return errors;
}

function toBr(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

function toPerson(person: RequestFormPerson) {
  return {
    name: person.name.trim(),
    // a tela mostra com máscara; o serviço recebe só os dígitos, como o perfil guarda.
    phone: digits(person.phone),
    email: person.email.trim() || null,
  };
}

export function toCreateInput(form: RequestFormState): CreateOnDemandRequestsInput {
  return {
    dates: form.dates.map((d) => ({ date: d.date })),
    quantity: Number(form.quantity),
    photoUrls: form.photoUrls,
    observations: form.observations.trim() || null,
    requester: toPerson(form.requester),
    companion: form.hasCompanion ? toPerson(form.companion) : null,
  };
}
